import { RoiCalculatorFormData, RoiFarmGateInputs } from "../../../models/RoiCalculator";
import { normalizeStageType, RoiFormStageDescriptor } from "../../../helpers/roiStageData";
import { StageType } from "../../../../../graphql/types";

interface ValidationError {
  field: string;
  type: string;
  message: string;
}

const FARM_GATE_REQUIRED_FIELDS: Partial<
  Record<StageType, ReadonlyArray<keyof RoiFarmGateInputs>>
> = {
  [StageType.Breeding]: ["spentHenRoosterPrice", "eggSellingPrice"],
  [StageType.Growing]: ["liveWeightPrice"],
  [StageType.Fattening]: ["liveWeightPrice"],
  [StageType.ContinuousGrowing]: ["liveWeightPrice"],
  [StageType.Laying]: ["consumptionEggPrice", "spentHen"],
  [StageType.Hatching]: ["dayOldChickPrice"],
};

interface FarmGateValidationContext {
  terminalOutputStageType: string | null;
  hasProcessingStage: boolean;
  farmGateDisabled: boolean;
}

export const validateRoiForm = (
  values: RoiCalculatorFormData,
  relevantStages: RoiFormStageDescriptor[],
  fieldRequiredMessage: string,
  farmGateContext?: FarmGateValidationContext
): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!values.currency) {
    errors.push({ field: "currency", type: "required", message: fieldRequiredMessage });
  }

  if (!values.inclusionCostOfSolution) {
    errors.push({ field: "inclusionCostOfSolution", type: "required", message: fieldRequiredMessage });
  }

  relevantStages.forEach((_stage, index) => {
    if (!values.stages?.[index]?.baselineInputs?.feedCost) {
      errors.push({ field: `stages.${index}.baselineInputs.feedCost`, type: "required", message: fieldRequiredMessage });
    }
    if (!values.stages?.[index]?.interventionInputs?.feedCost) {
      errors.push({ field: `stages.${index}.interventionInputs.feedCost`, type: "required", message: fieldRequiredMessage });
    }
  });

  if (
    farmGateContext &&
    !farmGateContext.hasProcessingStage &&
    !farmGateContext.farmGateDisabled &&
    farmGateContext.terminalOutputStageType
  ) {
    const stageKey = normalizeStageType(farmGateContext.terminalOutputStageType) as StageType;
    const requiredFields = FARM_GATE_REQUIRED_FIELDS[stageKey];
    if (requiredFields) {
      requiredFields.forEach((fieldName) => {
        if (!values.farmGate?.baselineInputs?.[fieldName]) {
          errors.push({
            field: `farmGate.baselineInputs.${fieldName}`,
            type: "required",
            message: fieldRequiredMessage,
          });
        }
        if (!values.farmGate?.interventionInputs?.[fieldName]) {
          errors.push({
            field: `farmGate.interventionInputs.${fieldName}`,
            type: "required",
            message: fieldRequiredMessage,
          });
        }
      });
    }
  }

  return errors;
};
